import { getRestaurants } from '@features/tables/hooks/useGetRestaurants';
import { getTablesAvailability } from '@features/tables/hooks/useGetTablesAvailability';

import { DiningDayAvailabilitySchema, DiningRestaurantSchema } from './schemas';
import type { DiningDayAvailability, DiningRestaurant } from './schemas';

function toDisplayTime(raw: string): string {
  const match = raw.match(/(\d{1,2}:\d{2})/);
  return match ? match[1] : raw;
}

export async function getDiningRestaurants(
  propertyId: number,
  language: string
): Promise<DiningRestaurant[]> {
  const restaurants = await getRestaurants({ propertyId, language });

  return DiningRestaurantSchema.array().parse(
    (restaurants ?? []).map((restaurant) => ({
      id: String(restaurant.config.id),
      providerId: String(restaurant.config.id),
      restaurantName: restaurant.restaurantName,
      configName: restaurant.config.name,
      restaurantId: restaurant.config.id,
      name: restaurant.config.name || restaurant.restaurantName,
      subtitle: restaurant.config.subtitle,
      description: restaurant.config.description,
      imageUrls: restaurant.config.imageGallery.map((image) => image.url),
      menus: restaurant.config.menu.map((menu) => ({
        id: String(menu.id),
        title: menu.name,
        url: menu.file.url,
      })),
      openingTime: toDisplayTime(restaurant.config.openingTime),
      closingTime: toDisplayTime(restaurant.config.closingTime),
      closedDates: (restaurant.closedDates?.closedDays ?? []).filter(
        (day): day is string => typeof day === 'string'
      ),
    }))
  );
}

export interface DiningAvailabilityParams {
  date: string;
  restaurantName: string;
  restaurantId: number;
  adults: number;
  children: number;
}

export async function getRestaurantAvailabilityForDate(
  propertyId: number,
  { date, restaurantName, restaurantId, adults, children }: DiningAvailabilityParams
): Promise<DiningDayAvailability> {
  const data = await getTablesAvailability({
    propertyId,
    date,
    restaurant: restaurantName,
    restaurantId,
    adults,
    children,
  });

  const timeSlots = data?.timeSlots ?? [];
  const promotions = data?.promotions ?? [];

  return DiningDayAvailabilitySchema.parse({
    timeslots: timeSlots.map((slot) => ({
      date,
      timeSlot: slot.timeSlot,
      startTime: toDisplayTime(slot.timeSlot),
      leaveTime: slot.leaveTime ?? null,
      hasStandardAvailability: slot.hasStandardAvailability,
      standardAvailabilityFeeAmount:
        slot.standardAvailabilityFeeAmount != null
          ? String(slot.standardAvailabilityFeeAmount)
          : null,
      timeslotReservationId: slot.timeslotReservationId ?? null,
      maxPartySize: slot.maxPartySize ?? null,
      minPartySize: slot.minPartySize ?? null,
      availablePromotions: (slot.availablePromotions ?? []).map((promotion) => ({
        id: String(promotion.id),
        timeslotReservationId: promotion.timeslotReservationId ?? null,
      })),
    })),
    promotions: promotions.map((promotion) => ({
      id: String(promotion.id),
      name: promotion.name,
    })),
  });
}
